import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  BellRing,
  Check,
  History,
  Loader2,
  Package,
  RefreshCw,
  Search,
  Truck,
  Volume2,
  VolumeX,
  X,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { PageContainer, PageHeader, Pill } from "@/components/layout/PageShell";
import {
  OnlineOrderDetailSheet,
  STATUS_TONES,
  formatStatus,
  sourceBadge,
} from "@/components/online/OnlineOrderDetailSheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/contexts/AuthContext";
import { useOnlineOrderInbox } from "@/hooks/useOnlineOrderInbox";
import api from "@/lib/api";
import { money } from "@/lib/format";
import {
  loadOnlineOrderSoundEnabled,
  setOnlineOrderSoundEnabled,
} from "@/lib/onlineOrderAlerts";
import { cn } from "@/lib/utils";

const STATUS_OPTIONS = [
  { id: "", label: "All active" },
  { id: "new", label: "New" },
  { id: "confirmed", label: "Confirmed" },
  { id: "preparing", label: "Preparing" },
  { id: "out_for_delivery", label: "Out for delivery" },
];

const SOURCE_OPTIONS = [
  { id: "all", labelKey: "onlineOrders.sourceAll" },
  { id: "storefront", labelKey: "onlineOrders.sourceOnline" },
  { id: "call", labelKey: "onlineOrders.sourceCall" },
];

const MODE_OPTIONS = [
  { id: "all", label: "All modes" },
  { id: "pickup", label: "Pickup" },
  { id: "delivery", label: "Delivery" },
];

function SortIcon({ column, sort, direction }) {
  if (sort !== column) return <ArrowUpDown className="h-3.5 w-3.5 opacity-40" />;
  return direction === "asc" ? (
    <ArrowUp className="h-3.5 w-3.5" />
  ) : (
    <ArrowDown className="h-3.5 w-3.5" />
  );
}

function useDebounced(value, delay = 350) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

function relativeTime(iso) {
  if (!iso) return "—";
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.max(0, Math.floor(diff / 60000));
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function statusAccent(status) {
  if (status === "new") return "bg-brand-orange";
  if (status === "out_for_delivery") return "bg-[#C2410C]";
  if (status === "preparing" || status === "confirmed") return "bg-amber-500";
  return "bg-slate-300";
}

function isAttentionRow(row) {
  if (row.status === "new") return true;
  return (
    row.mode === "delivery" &&
    ["confirmed", "preparing", "out_for_delivery"].includes(row.status)
  );
}

function KpiCard({ label, value, tone, icon: Icon, active, onClick, pulse }) {
  const tones = {
    new: "border-orange-200 bg-gradient-to-br from-orange-50 to-white text-orange-900",
    active: "border-[#E8D5C8] bg-white text-brand-brown",
    delivery: "border-amber-200 bg-gradient-to-br from-amber-50 to-white text-amber-900",
    history: "border-[#E8D5C8] bg-white text-brand-brown/80",
  };
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "relative overflow-hidden rounded-2xl border p-4 text-left shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md",
        tones[tone] || tones.active,
        active && "ring-2 ring-brand-orange/40 shadow-md",
        pulse && value > 0 && "ring-1 ring-orange-400/30",
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <p className="text-[10px] font-bold uppercase tracking-[0.16em] opacity-70">{label}</p>
        {Icon ? <Icon className="h-4 w-4 opacity-50" /> : null}
      </div>
      <p className="mt-2 text-3xl font-extrabold tracking-tight tabular-nums">{value}</p>
      {pulse && value > 0 ? (
        <span className="absolute right-3 top-3 h-2 w-2 rounded-full bg-rose-500 shadow-[0_0_0_4px_rgba(244,63,94,0.2)]" />
      ) : null}
    </button>
  );
}

export default function OnlineOrders() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const branchId = user?.default_branch?.id;
  const currency = user?.company_currency || user?.company?.currency || user?.default_branch?.currency || "AED";
  const { counts } = useOnlineOrderInbox();
  const [searchParams, setSearchParams] = useSearchParams();

  const attentionMode = searchParams.get("attention") === "1";
  const orderParam = searchParams.get("order");

  const [statusFilter, setStatusFilter] = useState("");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [modeFilter, setModeFilter] = useState("all");
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounced(search);
  const [sort, setSort] = useState("placed_at");
  const [direction, setDirection] = useState("desc");
  const [page, setPage] = useState(1);
  const [selectedId, setSelectedId] = useState(null);
  const [soundOn, setSoundOn] = useState(() => loadOnlineOrderSoundEnabled());
  const [shortcut, setShortcut] = useState(null);

  useEffect(() => {
    if (!orderParam) return;
    const id = Number(orderParam);
    if (id) setSelectedId(id);
    const next = new URLSearchParams(searchParams);
    next.delete("order");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderParam]);

  useEffect(() => {
    if (attentionMode) {
      setShortcut(null);
      setStatusFilter("");
      setModeFilter("all");
    }
  }, [attentionMode]);

  useEffect(() => {
    setPage(1);
  }, [statusFilter, sourceFilter, modeFilter, debouncedSearch, sort, direction, attentionMode, shortcut]);

  const { data: listData, isLoading, isFetching, refetch } = useQuery({
    queryKey: [
      "online-orders",
      branchId,
      "board",
      attentionMode,
      shortcut,
      statusFilter,
      sourceFilter,
      modeFilter,
      debouncedSearch,
      sort,
      direction,
      page,
    ],
    queryFn: async () => {
      const params = {
        branch_id: branchId,
        scope: "active",
        source: sourceFilter === "all" ? undefined : sourceFilter,
        search: debouncedSearch || undefined,
        sort,
        direction,
        page,
        per_page: 25,
      };

      if (attentionMode && !shortcut) {
        params.attention = 1;
      } else if (shortcut === "new") {
        params.status = "new";
      } else if (shortcut === "pending_delivery") {
        params.pending_delivery = 1;
      } else {
        params.status = statusFilter || undefined;
        params.mode = modeFilter === "all" ? undefined : modeFilter;
      }

      const { data: res } = await api.get("/online-orders", { params });
      return res;
    },
    enabled: Boolean(branchId),
    refetchInterval: 30000,
    placeholderData: keepPreviousData,
  });

  const orders = listData?.orders ?? [];
  const meta = listData?.meta ?? { current_page: 1, last_page: 1, total: 0 };

  const confirmMutation = useMutation({
    mutationFn: (id) =>
      api.post(
        `/online-orders/${id}/status`,
        { status: "confirmed" },
        { params: { branch_id: branchId } },
      ),
    onSuccess: () => {
      toast.success("Order confirmed");
      queryClient.invalidateQueries({ queryKey: ["online-orders"] });
      queryClient.invalidateQueries({ queryKey: ["online-orders-inbox"] });
    },
    onError: (err) => toast.error(err.response?.data?.message || "Could not confirm"),
  });

  const emptyMessage =
    sourceFilter === "call"
      ? t("onlineOrders.emptyCall")
      : sourceFilter === "storefront"
        ? t("onlineOrders.emptyOnline")
        : t("onlineOrders.emptyAll");

  const toggleSort = (column) => {
    if (sort === column) {
      setDirection((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSort(column);
      setDirection(column === "placed_at" || column === "net_amount" ? "desc" : "asc");
    }
  };

  const toggleSound = () => {
    const next = !soundOn;
    setSoundOn(next);
    setOnlineOrderSoundEnabled(next);
  };

  const clearAttention = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("attention");
    next.delete("order");
    setSearchParams(next, { replace: true });
    setShortcut(null);
  };

  const applyNewShortcut = () => {
    clearAttention();
    setShortcut("new");
    setStatusFilter("new");
    setModeFilter("all");
  };

  const applyPendingDelivery = () => {
    clearAttention();
    setShortcut("pending_delivery");
    setStatusFilter("");
    setModeFilter("delivery");
  };

  const applyActive = () => {
    clearAttention();
    setShortcut(null);
    setStatusFilter("");
    setModeFilter("all");
  };

  const columns = [
    { id: "reference", label: "Reference" },
    { id: "customer_name", label: "Customer" },
    { id: "mode", label: "Mode" },
    { id: "status", label: "Status" },
    { id: "net_amount", label: "Amount" },
    { id: "placed_at", label: "Placed" },
  ];

  const showAttentionBanner = attentionMode || shortcut === "pending_delivery" || shortcut === "new";

  return (
    <PageContainer>
      <PageHeader
        title={t("onlineOrders.title")}
        subtitle={t("onlineOrders.subtitle")}
        actions={
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={toggleSound}
              className="rounded-full border-[#E8D5C8] bg-white"
            >
              {soundOn ? <Volume2 className="h-4 w-4 text-brand-orange" /> : <VolumeX className="h-4 w-4" />}
              {soundOn ? "Sound on" : "Sound off"}
            </Button>
            <Button variant="outline" size="sm" asChild className="rounded-full border-[#E8D5C8] bg-white">
              <Link to="/online/history">
                <History className="h-4 w-4" />
                History
                {(counts.history || 0) > 0 ? (
                  <span className="ms-1 rounded-full bg-slate-100 px-1.5 text-[10px] font-semibold">
                    {counts.history}
                  </span>
                ) : null}
              </Link>
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => refetch()}
              disabled={isFetching}
              className="rounded-full border-[#E8D5C8] bg-white"
            >
              {isFetching ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              Refresh
            </Button>
          </div>
        }
      />

      <div className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <KpiCard
          label="New"
          value={counts.new || 0}
          tone="new"
          icon={BellRing}
          pulse
          active={shortcut === "new"}
          onClick={applyNewShortcut}
        />
        <KpiCard
          label="Active"
          value={counts.active || 0}
          tone="active"
          icon={Package}
          active={!attentionMode && !shortcut}
          onClick={applyActive}
        />
        <KpiCard
          label="Pending delivery"
          value={counts.pending_delivery || 0}
          tone="delivery"
          icon={Truck}
          pulse
          active={shortcut === "pending_delivery"}
          onClick={applyPendingDelivery}
        />
        <KpiCard
          label="History"
          value={counts.history || 0}
          tone="history"
          icon={History}
          onClick={() => navigate("/online/history")}
        />
      </div>

      {showAttentionBanner && (
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-orange-200 bg-gradient-to-r from-orange-50 via-white to-amber-50 px-4 py-3 shadow-sm">
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-brand-orange text-white shadow-sm">
              <BellRing className="h-4 w-4" />
            </span>
            <div>
              <p className="font-semibold text-brand-brown">
                {attentionMode
                  ? "Attention needed"
                  : shortcut === "new"
                    ? "New orders"
                    : "Pending delivery"}
              </p>
              <p className="text-xs text-slate-600">
                <span className="font-semibold text-brand-orange">{counts.new || 0} new</span>
                <span className="mx-1.5 text-slate-300">·</span>
                <span className="font-semibold text-amber-700">
                  {counts.pending_delivery || 0} pending delivery
                </span>
              </p>
            </div>
          </div>
          <Button
            size="sm"
            variant="ghost"
            className="rounded-full"
            onClick={() => {
              clearAttention();
              setShortcut(null);
            }}
          >
            <X className="h-4 w-4" />
            Clear
          </Button>
        </div>
      )}

      <div className="mb-4 rounded-2xl border border-[#E8D5C8] bg-white/90 p-3 shadow-sm backdrop-blur">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
          <div className="inline-flex rounded-full border border-slate-200 bg-slate-50 p-0.5">
            {SOURCE_OPTIONS.map((opt) => (
              <button
                key={opt.id}
                type="button"
                onClick={() => setSourceFilter(opt.id)}
                className={cn(
                  "rounded-full px-3 py-1.5 text-xs font-semibold transition-colors",
                  sourceFilter === opt.id
                    ? "bg-brand-brown text-white shadow-sm"
                    : "text-slate-600 hover:text-slate-900",
                )}
              >
                {t(opt.labelKey)}
              </button>
            ))}
          </div>

          <Select
            value={statusFilter || "__all__"}
            onValueChange={(v) => {
              setShortcut(null);
              if (attentionMode) clearAttention();
              setStatusFilter(v === "__all__" ? "" : v);
            }}
            disabled={attentionMode || Boolean(shortcut)}
          >
            <SelectTrigger className="h-9 w-full rounded-full lg:w-[180px]">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map((o) => (
                <SelectItem key={o.id || "all"} value={o.id || "__all__"}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={modeFilter}
            onValueChange={(v) => {
              setShortcut(null);
              if (attentionMode) clearAttention();
              setModeFilter(v);
            }}
            disabled={attentionMode || Boolean(shortcut)}
          >
            <SelectTrigger className="h-9 w-full rounded-full lg:w-[150px]">
              <SelectValue placeholder="Mode" />
            </SelectTrigger>
            <SelectContent>
              {MODE_OPTIONS.map((o) => (
                <SelectItem key={o.id} value={o.id}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <div className="relative min-w-[200px] flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t("onlineOrders.searchPlaceholder")}
              className="h-9 rounded-full border-slate-200 pl-9"
            />
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-brand-orange" />
        </div>
      ) : (
        <div className="overflow-hidden rounded-2xl border border-[#E8D5C8] bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-orange-50 bg-[#FFF7F0] text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500">
                  {columns.map((c) => (
                    <th key={c.id} className={cn("px-4 py-3", c.id === "net_amount" && "text-right")}>
                      <button
                        type="button"
                        className={cn(
                          "inline-flex items-center gap-1 hover:text-slate-800",
                          c.id === "net_amount" && "ms-auto",
                        )}
                        onClick={() => toggleSort(c.id)}
                      >
                        {c.label}
                        <SortIcon column={c.id} sort={sort} direction={direction} />
                      </button>
                    </th>
                  ))}
                  <th className="px-4 py-3">Source</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((row) => {
                  const highlight = isAttentionRow(row) && (attentionMode || Boolean(shortcut));
                  return (
                    <tr
                      key={row.id}
                      className={cn(
                        "group cursor-pointer border-b border-slate-100 transition-colors",
                        highlight
                          ? row.status === "new"
                            ? "bg-orange-50/70 hover:bg-orange-50"
                            : "bg-amber-50/50 hover:bg-amber-50/80"
                          : "hover:bg-orange-50/40",
                      )}
                      onClick={() => setSelectedId(row.id)}
                    >
                      <td className="relative px-4 py-3">
                        <span
                          className={cn(
                            "absolute inset-y-2 left-0 w-1 rounded-full",
                            statusAccent(row.status),
                          )}
                        />
                        <div className="font-mono text-xs font-semibold text-slate-800">
                          {row.reference}
                        </div>
                        {row.status === "new" && (
                          <span className="mt-0.5 inline-block text-[10px] font-bold uppercase tracking-wider text-brand-orange">
                            Needs confirm
                          </span>
                        )}
                        {row.sales_header_id && (
                          <span className="mt-0.5 block text-[10px] font-semibold text-emerald-700">
                            KOT linked
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-900">{row.customer_name}</div>
                        <div className="text-xs text-slate-500">{row.customer_phone}</div>
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={cn(
                            "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold capitalize",
                            row.mode === "delivery"
                              ? "bg-amber-50 text-amber-800"
                              : "bg-slate-100 text-slate-700",
                          )}
                        >
                          {row.mode === "delivery" ? (
                            <Truck className="h-3 w-3" />
                          ) : (
                            <Package className="h-3 w-3" />
                          )}
                          {row.mode}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <Pill tone={STATUS_TONES[row.status] || "default"}>
                          {formatStatus(row.status)}
                        </Pill>
                      </td>
                      <td className="px-4 py-3 text-right font-semibold tabular-nums text-slate-900">
                        {money(row.net_amount, row.currency_code || currency)}
                      </td>
                      <td
                        className="px-4 py-3 text-xs text-slate-500"
                        title={row.placed_at ? new Date(row.placed_at).toLocaleString() : ""}
                      >
                        {relativeTime(row.placed_at)}
                      </td>
                      <td className="px-4 py-3">{sourceBadge(row.source, t)}</td>
                      <td className="px-4 py-3 text-right">
                        {row.status === "new" ? (
                          <Button
                            size="sm"
                            className="h-8 rounded-full bg-brand-orange opacity-100 shadow-sm hover:bg-[#E05E10] lg:opacity-0 lg:group-hover:opacity-100"
                            disabled={confirmMutation.isPending}
                            onClick={(e) => {
                              e.stopPropagation();
                              confirmMutation.mutate(row.id);
                            }}
                          >
                            <Check className="h-3.5 w-3.5" />
                            Confirm
                          </Button>
                        ) : (
                          <span className="text-[11px] text-slate-400 opacity-0 group-hover:opacity-100">
                            Open →
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {orders.length === 0 && (
                  <tr>
                    <td colSpan={8} className="px-4 py-14 text-center text-slate-500">
                      {emptyMessage}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-2 border-t border-orange-50 bg-[#FFF7F0]/60 px-4 py-2.5 text-xs text-slate-500">
            <span>
              {meta.total
                ? `Showing ${meta.from ?? 0}–${meta.to ?? 0} of ${meta.total}`
                : "No results"}
            </span>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                className="rounded-full"
                disabled={page <= 1 || isFetching}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Previous
              </Button>
              <span className="tabular-nums">
                Page {meta.current_page || page} of {meta.last_page || 1}
              </span>
              <Button
                size="sm"
                variant="outline"
                className="rounded-full"
                disabled={page >= (meta.last_page || 1) || isFetching}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        </div>
      )}

      <OnlineOrderDetailSheet
        orderId={selectedId}
        branchId={branchId}
        currency={currency}
        onClose={() => setSelectedId(null)}
      />
    </PageContainer>
  );
}
